// DataFileVariablesTab.tsx
import React, { useMemo, useState } from "react"
import { Select, Collapse, Table, Tag, Tooltip } from "antd"
import type { ColumnsType } from "antd/es/table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRotateRight } from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import {
  EXPORT_VARIABLE_TYPES,
  VARIABLE_STYLE,
} from "types/engineExport.types"
import { ExportMapVariable } from "types/exportMap.types"
import { flattenGroups } from "features/QueryConsole/components/ExportMap/utils/exportMapParser"
import { useDataFileVariables } from "features/QuestionnaireEditor/hooks/Labels/useDataFileVariables"

/**
 * Props interface for the DataFileVariablesTab component
 */
interface DataFileVariablesTabProps {
  /** Project identifier */
  projectId: string
  /** Currently selected variant name ("" for master) */
  currentVariant: string
  /** Whether this tab is currently active (gates the datamap fetch) */
  isActive: boolean
  /** The available content height in pixels */
  contentHeight: number
  /** Saves the questionnaire (so freshly authored edits reach the datamap) */
  saveQuestionnaire: () => Promise<void>
}

/** Type → badge color, matching the Export Map variable card. */
const getTypeColor = (type: string): string => {
  switch (type) {
    case "CAT":
      return "#1890ff"
    case "BOOL":
      return "#52c41a"
    case "INT":
      return "#faad14"
    case "REAL":
      return "#eb2f96"
    case "TEXT":
      return "#722ed1"
    default:
      return "#666"
  }
}

/**
 * Builds the columns for a read-only variables table. `topLevel` is the section
 * the table belongs to so the Variable column can show the relative sub-path.
 */
const buildColumns = (topLevel: string | null): ColumnsType<ExportMapVariable> => [
  {
    title: "Code",
    dataIndex: "variableCode",
    key: "variableCode",
    width: 180,
    render: (code: string) => (
      <span className="font-mono text-[13px] text-gray-800">{code || "—"}</span>
    ),
  },
  {
    title: "Variable",
    key: "label",
    render: (_: unknown, record: ExportMapVariable) => {
      // Show the path relative to the section header when grouped
      const relative =
        topLevel && record.path.length > 1
          ? record.path.slice(1).join(" // ")
          : record.path[record.path.length - 1]
      return <span className="text-gray-900">{relative || record.fullLabel}</span>
    },
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    width: 90,
    render: (type: string) => (
      <Tag color={getTypeColor(type)} style={{ marginInlineEnd: 0 }}>
        {type}
      </Tag>
    ),
  },
  {
    title: "Value codes",
    key: "values",
    width: 130,
    render: (_: unknown, record: ExportMapVariable) =>
      record.categories?.length ? (
        <span className="text-gray-500 text-xs">
          {record.categories.length} value
          {record.categories.length === 1 ? "" : "s"}
        </span>
      ) : (
        <span className="text-gray-300 text-xs">—</span>
      ),
  },
]

/**
 * Renders the expandable value-code list for a variable.
 */
const renderValueCodes = (record: ExportMapVariable): React.ReactNode => {
  if (!record.categories?.length) return null
  return (
    <ul className="m-0 pl-5 list-disc space-y-0.5">
      {record.categories.map((cat) => (
        <li key={`${record.fullLabel}-${cat.code}-${cat.label}`}>
          <span className="font-mono text-[13px] text-gray-800">{cat.code}</span>
          <span className="text-gray-400"> = </span>
          <span className="text-gray-700">{cat.label}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * A read-only table of variables with expandable value codes.
 */
const VariablesTable: React.FC<{
  variables: ExportMapVariable[]
  topLevel: string | null
}> = ({ variables, topLevel }) => (
  <Table<ExportMapVariable>
    size="small"
    columns={buildColumns(topLevel)}
    dataSource={variables}
    rowKey="fullLabel"
    pagination={{ pageSize: 100, hideOnSinglePage: true, size: "small" }}
    expandable={{
      rowExpandable: (record) => !!record.categories?.length,
      expandedRowRender: renderValueCodes,
    }}
  />
)

/**
 * DataFileVariablesTab — a read-only overview of what the authored labels become
 * in the final data file. Each question and hidden question is shown with its
 * generated variable code, type, and (expandable) value codes, grouped by
 * section. Codes are sourced from the datamap, which the backend generates from
 * the saved questionnaire.
 *
 * @component
 */
const DataFileVariablesTab: React.FC<DataFileVariablesTabProps> = ({
  projectId,
  currentVariant,
  isActive,
  contentHeight,
  saveQuestionnaire,
}) => {
  const {
    varType,
    setVarType,
    labelStyle,
    setLabelStyle,
    variables,
    groups,
    rootVariables,
    isLoading,
    error,
    refetch,
  } = useDataFileVariables({ projectId, currentVariant, isActive })

  const [isSaving, setIsSaving] = useState<boolean>(false)

  // Flatten each top-level group into all of its descendant variables.
  const sections = useMemo(
    () =>
      groups.map((group) => ({
        key: group.path.join("//"),
        label: group.label,
        variables: flattenGroups([group]),
      })),
    [groups]
  )

  // Expand all sections when there are only a few; otherwise the first one.
  const defaultActiveKeys = useMemo(() => {
    if (variables.length <= 60) return sections.map((s) => s.key)
    return sections.length > 0 ? [sections[0].key] : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections])

  const handleSaveAndRefresh = async (): Promise<void> => {
    try {
      setIsSaving(true)
      await saveQuestionnaire()
      refetch()
    } catch {
      // saveQuestionnaire surfaces its own error notification
    } finally {
      setIsSaving(false)
    }
  }

  const collapseItems = sections.map((section) => ({
    key: section.key,
    label: (
      <span>
        <span className="font-semibold text-gray-800">{section.label}</span>
        <span className="text-gray-400 text-xs ml-2">
          {section.variables.length} variable
          {section.variables.length === 1 ? "" : "s"}
        </span>
      </span>
    ),
    children: <VariablesTable variables={section.variables} topLevel={section.label} />,
  }))

  /**
   * Renders the body (loading / error / empty / content)
   */
  const renderBody = (): React.ReactNode => {
    if (isLoading && variables.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full py-8">
          <Loader fontSize={48} color="#36C3ED" />
          Loading data file variables...
        </div>
      )
    }

    if (error && variables.length === 0) {
      return (
        <div className="py-8">
          <p className="text-center text-red-500">{error}</p>
          <div className="flex justify-center mt-3">
            <DataQButton type="secondary" onClick={refetch}>
              <FontAwesomeIcon icon={faRotateRight} className="mr-2" />
              Try again
            </DataQButton>
          </div>
        </div>
      )
    }

    if (variables.length === 0) {
      return (
        <div className="py-8">
          <p className="text-center text-gray-500">
            No data file variables found.
          </p>
          <p className="text-center text-gray-400 mt-2">
            Add question labels (Q Label:) or hidden questions (HIDDEN Label) to
            the questionnaire — these become the variables in your data file.
          </p>
        </div>
      )
    }

    return (
      <>
        {rootVariables.length > 0 && (
          <div className="mb-4">
            <VariablesTable variables={rootVariables} topLevel={null} />
          </div>
        )}
        {collapseItems.length > 0 && (
          <Collapse
            items={collapseItems}
            defaultActiveKey={defaultActiveKeys}
            bordered={false}
          />
        )}
      </>
    )
  }

  const sectionCount = sections.length + (rootVariables.length > 0 ? 1 : 0)

  return (
    <div className="bg-white flex flex-col" style={{ height: contentHeight }}>
      {/* Controls + explanation */}
      <div className="px-1 pb-3 border-b">
        <p className="text-sm text-gray-600 mb-3">
          This is what your questionnaire labels become in the final data file.
          Each question and hidden question is mapped to a variable code; lists,
          range lists and exceptions are building blocks and don&apos;t appear
          here.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 mb-1">
              Use as variable names
            </span>
            <Select
              value={varType}
              onChange={setVarType}
              style={{ width: 260 }}
              options={[
                {
                  value: EXPORT_VARIABLE_TYPES.NUMBERING,
                  label: "Numbered automatically",
                },
                {
                  value: EXPORT_VARIABLE_TYPES.LABELS,
                  label: "Generate from questionnaire labels",
                },
              ]}
            />
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 mb-1">Variable style</span>
            <Select
              value={labelStyle}
              onChange={setLabelStyle}
              style={{ width: 240 }}
              options={[
                {
                  value: VARIABLE_STYLE.DEFAULT,
                  label: "Default (s/p prefixes)",
                },
                {
                  value: VARIABLE_STYLE.DECIPHER,
                  label: "Decipher standard (r/c prefixes)",
                },
              ]}
            />
          </div>
          <div className="ml-auto flex items-center gap-3">
            {variables.length > 0 && (
              <span className="text-sm text-gray-500">
                {variables.length} variable{variables.length === 1 ? "" : "s"}
                {sectionCount > 0
                  ? ` · ${sectionCount} section${sectionCount === 1 ? "" : "s"}`
                  : ""}
              </span>
            )}
            <Tooltip title="This overview reflects the last saved version of the questionnaire. Save your recent edits, then refresh to update it.">
              <span className="text-xs text-gray-400 cursor-default">
                Last saved version
              </span>
            </Tooltip>
            <DataQButton
              type="secondary"
              onClick={handleSaveAndRefresh}
              isLoading={isSaving}
            >
              <FontAwesomeIcon icon={faRotateRight} className="mr-2" />
              Save &amp; refresh
            </DataQButton>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-grow overflow-auto px-1 pt-3">{renderBody()}</div>
    </div>
  )
}

export default DataFileVariablesTab
